import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useContracts } from "@/hooks/useContracts";
import { Contract, ContractGroup } from "@/lib/types";
import { toast } from "sonner";
import { SortDirection, SortableColumn, FilterState, getExpiryStatusText, getDaysUntilExpiry, EXPIRY_STATUS_OPTIONS } from "./types";
import { useContractGroups } from "@/hooks/useContractGroups";

export function useContractsTable(selectedColumnsFromProps: string[]) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContracts, setSelectedContracts] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  
  const [filters, setFilters] = useState<FilterState>({
    importance: null,
    type: null,
    category: null,
    expiryDateRange: null,
    expiryStatus: null,
    groups: null
  });
  
  const longPressTimeoutRef = useRef<number | null>(null);
  const [isLongPressing, setIsLongPressing] = useState(false);
  
  const { useContractsQuery, useDeleteContractMutation } = useContracts();
  const { useContractGroupsQuery, useAssignContractsToGroupMutation, useRemoveContractsFromGroupMutation } = useContractGroups();
  const { data: contracts = [], isLoading, isError } = useContractsQuery();
  const { data: contractGroups = [] } = useContractGroupsQuery();
  const deleteContractMutation = useDeleteContractMutation();
  const assignToGroupMutation = useAssignContractsToGroupMutation();
  const removeFromGroupMutation = useRemoveContractsFromGroupMutation();

  const uniqueTypes = useMemo(() => {
    const types = new Set(contracts.map(contract => contract.type.name));
    return Array.from(types);
  }, [contracts]);

  const uniqueCategories = useMemo(() => {
    const categories = new Set(contracts.map(contract => contract.category?.name).filter(Boolean));
    return Array.from(categories as Set<string>);
  }, [contracts]);

  const filteredContracts = useMemo(() => {
    return contracts.filter(contract => {
      const matchesSearch = 
        contract.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.category?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.type.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      if (filters.importance && filters.importance.length > 0) {
        if (!filters.importance.includes(contract.importance)) return false;
      }
      
      if (filters.type && filters.type.length > 0) {
        if (!filters.type.includes(contract.type.name)) return false;
      }
      
      if (filters.category && filters.category.length > 0) {
        if (!contract.category || !filters.category.includes(contract.category.name)) return false;
      }
      
      if (filters.expiryDateRange) {
        const { start, end } = filters.expiryDateRange;
        if (start && contract.expiryDate < start) return false;
        if (end && contract.expiryDate > end) return false;
      }
      
      if (filters.expiryStatus && filters.expiryStatus.length > 0) {
        const status = getExpiryStatusText(contract);
        if (!filters.expiryStatus.includes(status)) return false;
      }
      
      if (filters.groups && filters.groups.length > 0) {
        if (!contract.groupId) return false;
        return filters.groups.includes(contract.groupId);
      }
      
      return true;
    });
  }, [contracts, searchTerm, filters]);

  const sortedContracts = useMemo(() => {
    if (!sortColumn || !sortDirection) return filteredContracts;
    
    return [...filteredContracts].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortColumn) {
        case "supplierName":
          valueA = a.supplierName.toLowerCase();
          valueB = b.supplierName.toLowerCase();
          break;
        case "importance":
          valueA = a.importance;
          valueB = b.importance;
          break;
        case "type":
          valueA = a.type.name.toLowerCase();
          valueB = b.type.name.toLowerCase();
          break;
        case "category":
          valueA = a.category?.name?.toLowerCase() || "";
          valueB = b.category?.name?.toLowerCase() || "";
          break;
        case "expiryDate":
          valueA = a.expiryDate.getTime();
          valueB = b.expiryDate.getTime();
          break;
        case "signedDate":
          valueA = a.signedDate.getTime();
          valueB = b.signedDate.getTime();
          break;
        case "expectedCost":
          valueA = a.expectedCost;
          valueB = b.expectedCost;
          break;
        case "expiryStatus":
          valueA = getDaysUntilExpiry(a.expiryDate);
          valueB = getDaysUntilExpiry(b.expiryDate);
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
      if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredContracts, sortColumn, sortDirection]);

  const paginatedContracts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedContracts.slice(startIndex, startIndex + pageSize);
  }, [sortedContracts, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(sortedContracts.length / pageSize);
  }, [sortedContracts, pageSize]);

  const resetPageIfNeeded = () => {
    if (currentPage > 1 && (currentPage - 1) * pageSize >= sortedContracts.length) {
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    resetPageIfNeeded();
  }, [filters, searchTerm, pageSize, sortedContracts.length]);

  const toggleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortDirection(null);
        setSortColumn(null);
      } else {
        setSortDirection("asc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const resetFilters = () => {
    setFilters({
      importance: null,
      type: null,
      category: null,
      expiryDateRange: null,
      expiryStatus: null,
      groups: null
    });
    setSearchTerm("");
    toast.success("Filters have been reset");
  };

  const toggleFilter = (filterType: keyof FilterState, value: string | number | { start: Date | null; end: Date | null }) => {
    setFilters(prev => {
      if (filterType === 'importance' || filterType === 'type' || filterType === 'category' || filterType === 'expiryStatus' || filterType === 'groups') {
        if (filterType === 'importance') {
          const importanceValue = Number(value);
          
          if (isNaN(importanceValue)) {
            console.error("Invalid importance value:", value);
            return prev;
          }
          
          const currentValues = prev.importance || [];
          
          if (currentValues.includes(importanceValue)) {
            return {
              ...prev,
              importance: currentValues.filter(v => v !== importanceValue)
            };
          } else {
            return {
              ...prev,
              importance: [...currentValues, importanceValue]
            };
          }
        } 
        else if (filterType === 'type') {
          const typeValue = value as string;
          const currentValues = prev.type || [];
          
          if (currentValues.includes(typeValue)) {
            return {
              ...prev,
              type: currentValues.filter(v => v !== typeValue)
            };
          } else {
            return {
              ...prev,
              type: [...currentValues, typeValue]
            };
          }
        }
        else if (filterType === 'category') {
          const categoryValue = value as string;
          const currentValues = prev.category || [];
          
          if (currentValues.includes(categoryValue)) {
            return {
              ...prev,
              category: currentValues.filter(v => v !== categoryValue)
            };
          } else {
            return {
              ...prev,
              category: [...currentValues, categoryValue]
            };
          }
        }
        else if (filterType === 'expiryStatus') {
          const statusValue = value as string;
          const currentValues = prev.expiryStatus || [];
          
          if (currentValues.includes(statusValue)) {
            return {
              ...prev,
              expiryStatus: currentValues.filter(v => v !== statusValue)
            };
          } else {
            return {
              ...prev,
              expiryStatus: [...currentValues, statusValue]
            };
          }
        }
        else if (filterType === 'groups') {
          const groupValue = value as string;
          const currentValues = prev.groups || [];
          
          if (currentValues.includes(groupValue)) {
            return {
              ...prev,
              groups: currentValues.filter(v => v !== groupValue)
            };
          } else {
            return {
              ...prev,
              groups: [...currentValues, groupValue]
            };
          }
        }
      }
      
      if (filterType === 'expiryDateRange') {
        return {
          ...prev,
          expiryDateRange: value as { start: Date | null; end: Date | null }
        };
      }
      
      return prev;
    });
  };

  const toggleContractSelection = (contractId: string) => {
    const newSelected = new Set(selectedContracts);
    if (newSelected.has(contractId)) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }
    setSelectedContracts(newSelected);
  };

  const toggleAllSelection = () => {
    if (selectedContracts.size === paginatedContracts.length) {
      setSelectedContracts(new Set());
    } else {
      setSelectedContracts(
        new Set(paginatedContracts.map((contract) => contract.id))
      );
    }
  };

  const handleDeleteContract = async (id: string) => {
    try {
      await deleteContractMutation.mutateAsync(id);
      if (selectedContracts.has(id)) {
        const newSelected = new Set(selectedContracts);
        newSelected.delete(id);
        setSelectedContracts(newSelected);
      }
    } catch (error) {
      console.error("Error deleting contract:", error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      for (const id of selectedContracts) {
        await deleteContractMutation.mutateAsync(id);
      }
      setSelectedContracts(new Set());
    } catch (error) {
      console.error("Error deleting contracts in bulk:", error);
    }
  };

  const handleContractTouchStart = (contract: Contract, event: React.TouchEvent | React.MouseEvent) => {
    if (
      event.target instanceof HTMLElement && 
      (event.target.closest('.contract-checkbox') || 
       event.target.closest('.contract-actions'))
    ) {
      return;
    }
    
    longPressTimeoutRef.current = window.setTimeout(() => {
      setIsLongPressing(true);
      navigate(`/contracts/${contract.id}/edit`);
      toast.info("Opening contract for editing");
      
      longPressTimeoutRef.current = null;
      setIsLongPressing(false);
    }, 800);
  };
  
  const handleContractTouchEnd = () => {
    if (longPressTimeoutRef.current !== null) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };
  
  const handleContractTouchMove = () => {
    if (longPressTimeoutRef.current !== null) {
      clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
      setIsLongPressing(false);
    }
  };
  
  const handleContractTap = (contract: Contract, event: React.MouseEvent) => {
    if (
      event.target instanceof HTMLElement && 
      (event.target.closest('.contract-checkbox') || 
       event.target.closest('.contract-actions'))
    ) {
      return;
    }
    
    if (!isLongPressing && longPressTimeoutRef.current === null) {
      navigate(`/contracts/${contract.id}`);
    }
  };

  const isColumnSelected = (id: string) => {
    return selectedColumnsFromProps.includes(id);
  };

  const hasActiveFilters = () => {
    return (
      (filters.importance && filters.importance.length > 0) ||
      (filters.type && filters.type.length > 0) ||
      (filters.category && filters.category.length > 0) ||
      (filters.expiryStatus && filters.expiryStatus.length > 0) ||
      (filters.groups && filters.groups.length > 0) ||
      (filters.expiryDateRange && (
        filters.expiryDateRange.start !== null || 
        filters.expiryDateRange.end !== null
      )) ||
      searchTerm.length > 0
    );
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  return {
    searchTerm,
    setSearchTerm,
    selectedContracts,
    setSelectedContracts,
    sortColumn,
    sortDirection,
    filters,
    isLongPressing,
    isLoading,
    isError,
    uniqueTypes,
    uniqueCategories,
    contracts,
    contractGroups,
    filteredContracts,
    sortedContracts,
    paginatedContracts,
    currentPage,
    pageSize,
    totalPages,
    handlePageSizeChange,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    toggleSort,
    resetFilters,
    toggleFilter,
    toggleContractSelection,
    toggleAllSelection,
    handleDeleteContract,
    handleBulkDelete,
    handleContractTouchStart,
    handleContractTouchEnd,
    handleContractTouchMove,
    handleContractTap,
    isColumnSelected,
    hasActiveFilters
  };
}
